import React, { useContext, useMemo, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { postRequest } from '../../../api/ApiController';
import { DatabaseContext } from '../../../redux/DatabaseStore';
import AuthContext from '../../AuthContext';
import { LocaleContext } from '../../LocaleContext';
import UtilContext from '../../UtilContext';

import { crmStateMatchesTicketType } from '../../../utils/crmCommunicationTypeAliases';
import { isCrmApprovalLevelBlockedBySequence, isCurrentUserInCrmTicketAwaitingUser } from '../../../utils/crmCommunicationAwaitingUser';
import {
  canUserCloseCrmTicketFromApprovalRejection,
  findCrmClosedStateId,
  ticketStatusMatchesManualClosure,
} from '../../../utils/crmCommunicationClosure';
import { CrmPanelShell } from './panelLayout';
import { runCrmPanelMutation, showCrmWorkflowToast } from '../../../utils/crmCommunicationPanelSave';

/**
 * @param {{ ticketId: number, ticket?: Record<string, unknown>|null, ticketType?: string, approvalRequests: Array<Record<string, unknown>> | undefined, onChanged: () => void, readOnly?: boolean, embedded?: boolean }} props
 */
export default function CrmCommunicationApprovalsPanel({
  ticketId,
  ticket = null,
  ticketType = 'announcement',
  approvalRequests,
  onChanged,
  onCloseTicket = null,
  readOnly = false,
  embedded = false,
}) {
  const [state] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const { t } = useContext(LocaleContext);
  const { toast } = useContext(UtilContext);
  const [busy, setBusy] = useState(false);

  const allUsers = Array.isArray(state?.users) ? state.users : [];
  const levels = useMemo(
    () => (Array.isArray(approvalRequests) ? approvalRequests.slice().sort((a, b) => {
      const ra = Number(a?.level_def?.rating ?? a?.level_def?.sort_order ?? a?.level ?? 9999);
      const rb = Number(b?.level_def?.rating ?? b?.level_def?.sort_order ?? b?.level ?? 9999);
      return ra - rb;
    }) : []),
    [approvalRequests],
  );

  const approvalStates = useMemo(() => {
    const all = Array.isArray(state?.crm_states) ? state.crm_states : [];
    return all
      .filter((s) => String(s?.phase || '') === 'approval')
      .filter((s) => {
        const types = Array.isArray(s?.applicable_types) ? s.applicable_types : null;
        return crmStateMatchesTicketType(types, ticketType);
      })
      .sort((a, b) => Number(a?.sort_order || 0) - Number(b?.sort_order || 0));
  }, [state?.crm_states, ticketType]);

  const hasAdminOverride = useMemo(() => {
    if (user?.is_superuser) return true;
    const roles = Array.isArray(user?.roles) ? user.roles : [];
    return roles.some((r) => (typeof r === 'string' ? r.toLowerCase() === 'admin' : String(r?.name || '').toLowerCase() === 'admin'));
  }, [user?.is_superuser, user?.roles]);

  const inAwaitingUser = useMemo(
    () => isCurrentUserInCrmTicketAwaitingUser(ticket, user),
    [ticket, user],
  );

  const closedStateId = useMemo(
    () => findCrmClosedStateId(state?.crm_states),
    [state?.crm_states],
  );

  const showCloseButton = useMemo(
    () => ticketStatusMatchesManualClosure(state?.crm_states, ticket, 'approval', ticketType),
    [state?.crm_states, ticket, ticketType],
  );

  const canCloseTicket = useMemo(
    () => canUserCloseCrmTicketFromApprovalRejection(ticket, user, state, ticketType),
    [ticket, user, state, ticketType],
  );

  const stateLabel = (row) => {
    const n = row?.name;
    if (n && typeof n === 'object') return n.sk || n.en || n.de || '';
    return n || '';
  };

  const levelLabel = (row) => {
    const fromLevel = row?.level_def?.name;
    if (fromLevel && typeof fromLevel === 'string') return fromLevel;
    return `${t('CrmCommunication.Detail.Level')} ${row?.level ?? ''}`;
  };

  const isSectionFinal = (row) => {
    const transition = String(row?.approval_state?.transition_type || '');
    const phase = String(row?.approval_state?.phase || '');
    return phase === 'approval' && transition === 'section_final';
  };

  const isRejected = (row) => {
    const transition = String(row?.approval_state?.transition_type || '');
    const phase = String(row?.approval_state?.phase || '');
    return phase === 'approval' && transition === 'final_manually';
  };

  const isBlockedBySequence = (idx) => isCrmApprovalLevelBlockedBySequence(levels, approvalStates, idx);

  const canSetApprover = (row, idx) => {
    if (readOnly || busy || isBlockedBySequence(idx)) return false;
    if (row?.approval_state_id != null && row?.approval_state_id !== '') return false;
    if (hasAdminOverride) return true;
    return inAwaitingUser;
  };

  const canSetState = (row, idx) => {
    if (readOnly || busy || isBlockedBySequence(idx)) return false;
    if (!row?.approver_id) return false;
    if (row?.approval_state_id != null && row?.approval_state_id !== '') return false;
    if (isSectionFinal(row) || isRejected(row)) return false;
    return Number(row.approver_id) === Number(user?.id);
  };

  const setApprover = async (row, approverId) => {
    setBusy(true);
    try {
      await runCrmPanelMutation(onChanged, async () => {
        const res = await postRequest(`/api/crm_communication_tickets/${ticketId}/set_approver`, { level_id: row.level_id, approver_id: approverId }, true);
        if (res?.status === 200) {
          showCrmWorkflowToast(toast, t, 'ServiceTaskDetail.Toast.AssigneeSaved');
          return res;
        } else {
          throw new Error(String(res?.message || 'Error'));
        }
      });
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('CrmCommunication.Toast.Error'), detail: String(e?.message || e), life: 4000 });
    } finally {
      setBusy(false);
    }
  };

  const setApprovalState = async (row, approvalStateId) => {
    setBusy(true);
    try {
      await runCrmPanelMutation(onChanged, async () => {
        const res = await postRequest(`/api/crm_communication_tickets/${ticketId}/set_approver_state`, { level_id: row.level_id, approval_state_id: approvalStateId }, true);
        if (res?.status === 200) {
          showCrmWorkflowToast(toast, t, 'ServiceTaskDetail.Toast.StateSaved');
          return res;
        } else {
          throw new Error(String(res?.message || 'Error'));
        }
      });
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('CrmCommunication.Toast.Error'), detail: String(e?.message || e), life: 4000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <CrmPanelShell
      embedded={embedded}
      title={t('CrmCommunication.Detail.Approvals')}
      t={t}
    >
      <ul className={`${embedded ? 'mt-1' : 'mt-3'} space-y-3`}>
        {levels.length === 0 ? <li>{t('CrmCommunication.Detail.NoData')}</li> : null}
        {levels.map((row, idx) => {
          const allowedApprovers = Array.isArray(row?.possible_approvers) ? row.possible_approvers.map((x) => Number(x)) : [];
          const approverOptions = allUsers
            .filter((u) => allowedApprovers.includes(Number(u.id)))
            .map((u) => ({ label: u?.employee?.name || u?.username || `#${u.id}`, value: u.id }));

          const stateOptions = approvalStates.map((s) => ({ label: stateLabel(s), value: s.id }));

          return (
            <li key={String(row.id || row.level_id || idx)} className="flex flex-col mt-2 mb-5">
              <div className="flex flex-col">
                <label className="font-bold mb-1">
                  {t('ServiceTaskDetail.Approval')} - {levelLabel(row)}
                </label>
                {!row?.approval_state_id ? (
                  <label className="font-normal text-md mb-1">
                    {t('ServiceTask.WaitingForApproval')}
                  </label>
                ) : null}
                <Dropdown
                  value={row?.approver_id != null ? Number(row.approver_id) : null}
                  options={approverOptions}
                  onChange={(e) => void setApprover(row, e.value)}
                  disabled={!canSetApprover(row, idx)}
                  optionLabel="label"
                  optionValue="value"
                  filter
                  placeholder={t('ServiceTaskDetail.SelectEmployee')}
                  className="w-60 px-2 py-0 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                />
                <div className="mt-2">
                  <label className="font-normal text-md mb-1">{t('ServiceTask.StatusLabel')}</label>
                  <select
                    value={row?.approval_state_id != null ? String(row.approval_state_id) : ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (!val) return;
                      void setApprovalState(row, Number(val));
                    }}
                    disabled={!canSetState(row, idx)}
                    className="block w-60 px-4 py-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                  >
                    <option value="">{t('Common.SelectOption')}</option>
                    {stateOptions.map((opt) => (
                      <option key={String(opt.value)} value={String(opt.value)}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </li>
          );
        })}
        {showCloseButton ? (
          <li className="flex flex-col mt-2 mb-5">
            <button
              type="button"
              disabled={!canCloseTicket || busy || !closedStateId || typeof onCloseTicket !== 'function'}
              onClick={() => onCloseTicket(closedStateId)}
              className="flex justify-center items-center mt-2 w-60 h-10 font-medium px-2 py-1 tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none focus:bg-zinc-600 disabled:opacity-25"
            >
              {t('ServiceTaskDetail.Close')}
            </button>
          </li>
        ) : null}
      </ul>
    </CrmPanelShell>
  );
}
